import { OrderLineOnHoldReason, OrderLineStatus } from '@admin-panel/types';

type OnHoldLineShape = {
  onhold?: boolean | null;
  onholdReason?: OrderLineOnHoldReason | string | null;
  status?: OrderLineStatus | string | null;
};

const ON_HOLD_REASON_LABELS: Record<OrderLineOnHoldReason, string> = {
  [OrderLineOnHoldReason.NA]: '',
  [OrderLineOnHoldReason.PENDING_ALTERATION]: 'PENDING ALTERATION',
  [OrderLineOnHoldReason.PENDING_CUSTOMISATION_REMARKS]: 'PENDING CUSTOMISATION REMARKS',
  [OrderLineOnHoldReason.OUT_OF_STOCK]: 'OUT OF STOCK',
  [OrderLineOnHoldReason.COD_CONFIRMATION_PENDING]: 'COD CONFIRMATION PENDING',
  [OrderLineOnHoldReason.RETURN_PENDING]: 'RETURN PENDING',
  [OrderLineOnHoldReason.FREQUENT_RETURNER]: 'FREQUENT RETURNER',
  [OrderLineOnHoldReason.TRY_AT_HOME_CONSOLIDATION]: 'TRY AT HOME CONSOLIDATION',
  [OrderLineOnHoldReason.UNICOM_DATA_VALIDATION_ERR]: 'UNICOM DATA VALIDATION ERROR',
};

const normalizeReason = (reason?: string | null): string =>
  reason && reason !== OrderLineOnHoldReason.NA ? reason : OrderLineOnHoldReason.NA;

/**
 * Mirrors Fulfillment's active business-hold definition: cancelling a held line
 * does not clear `onhold`, so a CANCELLED line is never treated as held.
 */
export const isOrderLineActivelyOnHold = (
  line: OnHoldLineShape | null | undefined,
  reason?: OrderLineOnHoldReason,
): boolean => {
  if (!line || line.onhold !== true || line.status === OrderLineStatus.CANCELLED) {
    return false;
  }
  return reason === undefined || line.onholdReason === reason;
};

export const hasActiveOnHoldLine = (
  lines: Array<OnHoldLineShape | null | undefined> | null | undefined,
  reason?: OrderLineOnHoldReason,
): boolean => (lines ?? []).some(line => isOrderLineActivelyOnHold(line, reason));

/**
 * Distinct active hold reasons across every line of an order. Line order from
 * the API is not stable, so never derive order-level hold state from one line.
 * A held line without a specific reason is reported as `NA`.
 */
export const getActiveOnHoldReasons = (
  lines: Array<OnHoldLineShape | null | undefined> | null | undefined,
): Array<string> => {
  const reasons = new Set<string>();
  (lines ?? []).forEach(line => {
    if (isOrderLineActivelyOnHold(line)) {
      reasons.add(normalizeReason(line?.onholdReason));
    }
  });
  return Array.from(reasons).sort();
};

export const formatOnHoldReasonLabel = (reason?: string | null): string => {
  const normalized = normalizeReason(reason);
  if (normalized in ON_HOLD_REASON_LABELS) {
    return ON_HOLD_REASON_LABELS[normalized as OrderLineOnHoldReason];
  }
  return normalized.replace(/_+/g, ' ').trim().toUpperCase();
};
